interface ReadingSettings {
  fontSize: number;
  lineHeight: number;
  width: number;
}

interface ReadingSettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  settings: ReadingSettings;
  onChange: (next: ReadingSettings) => void;
}

const DEFAULT_SETTINGS: ReadingSettings = {
  fontSize: 18,
  lineHeight: 1.85,
  width: 640,
};

const FONT_SIZES = [
  { label: 'S', value: 16 },
  { label: 'M', value: 18 },
  { label: 'L', value: 20 },
];

const LINE_HEIGHTS = [
  { label: 'Tight', value: 1.6 },
  { label: 'Balanced', value: 1.8 },
  { label: 'Airy', value: 2.0 },
];

const WIDTHS = [
  { label: 'Narrow', value: 520 },
  { label: 'Medium', value: 640 },
  { label: 'Wide', value: 760 },
];

export function ReadingSettingsDrawer({
  isOpen,
  onClose,
  settings,
  onChange,
}: ReadingSettingsDrawerProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" />
      <div
        className="relative bg-gradient-to-t from-[var(--bg-primary)] to-[var(--bg-secondary)] rounded-t-3xl p-6 max-h-[80vh] overflow-hidden flex flex-col shadow-2xl animate-slide-up"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="w-12 h-1 bg-[var(--border)] rounded-full mx-auto mb-6 shrink-0" />

        <div className="flex items-center justify-between mb-6 shrink-0">
          <div>
            <h3 className="font-body text-lg font-medium text-[var(--text-primary)]">Reading settings</h3>
            <p className="font-ui text-xs text-[var(--text-tertiary)] mt-1">Adjust for comfort</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-6 overflow-y-auto pb-6">
          <section>
            <p className="font-ui text-[11px] text-[var(--text-tertiary)] uppercase tracking-[0.3em] mb-3">
              Text size
            </p>
            <div className="flex gap-2">
              {FONT_SIZES.map((option) => {
                const isActive = settings.fontSize === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => onChange({ ...settings, fontSize: option.value })}
                    className={`flex-1 py-3 rounded-2xl font-ui text-sm transition-all ${
                      isActive
                        ? 'bg-[var(--accent-soft)] text-[var(--accent)] ring-1 ring-[var(--accent)]/40'
                        : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </section>

          <section>
            <p className="font-ui text-[11px] text-[var(--text-tertiary)] uppercase tracking-[0.3em] mb-3">
              Line height
            </p>
            <div className="grid grid-cols-3 gap-2">
              {LINE_HEIGHTS.map((option) => {
                const isActive = settings.lineHeight === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => onChange({ ...settings, lineHeight: option.value })}
                    className={`py-3 rounded-2xl font-ui text-xs transition-all ${
                      isActive
                        ? 'bg-[var(--accent-soft)] text-[var(--accent)] ring-1 ring-[var(--accent)]/40'
                        : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </section>

          <section>
            <p className="font-ui text-[11px] text-[var(--text-tertiary)] uppercase tracking-[0.3em] mb-3">
              Page width
            </p>
            <div className="grid grid-cols-3 gap-2">
              {WIDTHS.map((option) => {
                const isActive = settings.width === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => onChange({ ...settings, width: option.value })}
                    className={`py-3 rounded-2xl font-ui text-xs transition-all ${
                      isActive
                        ? 'bg-[var(--accent-soft)] text-[var(--accent)] ring-1 ring-[var(--accent)]/40'
                        : 'bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </section>
        </div>

        <button
          onClick={() => onChange(DEFAULT_SETTINGS)}
          className="w-full mt-2 py-3 rounded-2xl font-ui text-xs text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
        >
          Reset to default
        </button>
      </div>
    </div>
  );
}
